import React, { useState, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { USAGE_TYPES } from './categories';

interface ParsedRow {
  [key: string]: string;
}

interface MappedProject {
  name: string;
  street: string;
  zipCode: string;
  city: string;
  usageType: string;
  usageSubType: string;
  owner: string;
  description: string;
}

interface ColumnMapping {
  name: string;
  street: string;
  zipCode: string;
  city: string;
  usageType: string;
  usageSubType: string;
  owner: string;
  description: string;
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Name *',
  street: 'Straße *',
  zipCode: 'PLZ',
  city: 'Stadt',
  usageType: 'Nutzungsart',
  usageSubType: 'Unterkategorie',
  owner: 'Eigentümer',
  description: 'Beschreibung',
}

const AUTO_DETECT_KEYWORDS: Record<string, string[]> = {
  name: ['name', 'bezeichnung', 'title', 'objekt', 'liegenschaft'],
  street: ['straße', 'strasse', 'street', 'adresse', 'address', 'str.', 'str '],
  zipCode: ['plz', 'postleitzahl', 'zip', 'postal'],
  city: ['stadt', 'city', 'ort', 'gemeinde', 'location'],
  usageType: ['nutzungsart', 'nutzung', 'typ', 'type', 'kategorie'],
  usageSubType: ['unterkategorie', 'subtyp', 'untertyp'],
  owner: ['eigentümer', 'eigentuemer', 'owner', 'besitzer', 'träger'],
  description: ['beschreibung', 'description', 'notiz', 'anmerkung', 'bemerkung'],
}

const autoDetectMapping = (columns: string[]): ColumnMapping => {
  const mapping: ColumnMapping = {
    name: '', street: '', zipCode: '', city: '',
    usageType: '', usageSubType: '', owner: '', description: ''
  }

  columns.forEach(col => {
    const colLower = col.toLowerCase()
    Object.entries(AUTO_DETECT_KEYWORDS).forEach(([field, keywords]) => {
      if (!mapping[field as keyof ColumnMapping]) {
        if (keywords.some(kw => colLower.includes(kw))) {
          mapping[field as keyof ColumnMapping] = col
        }
      }
    })
  })

  return mapping
}

// ─── Template download ────────────────────────────────────────────────────────

const downloadTemplate = () => {
  const ws = XLSX.utils.aoa_to_sheet([
    ['Name', 'Straße', 'PLZ', 'Stadt', 'Nutzungsart', 'Unterkategorie', 'Eigentümer', 'Beschreibung'],
    ['Gesamtschule Mitte', 'Falkstraße 44', '47058', 'Duisburg', 'Bildung', 'Weiterführende Schule', 'Stadt Duisburg', 'Hauptgebäude mit Turnhalle'],
    ['Rathaus Nord', 'Musterstraße 1', '47059', 'Duisburg', 'Verwaltung', 'Rathaus', 'Stadt Duisburg', ''],
  ])
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Liegenschaften')
  XLSX.writeFile(wb, 'Portalis_Liegenschaften_Vorlage.xlsx')
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  customers: { id: string; name: string }[];
  onClose: () => void;
  onImported: () => void;
}

const ProjectImport: React.FC<Props> = ({ customers, onClose, onImported }) => {
  const [step, setStep] = useState<'upload' | 'mapping' | 'preview' | 'importing' | 'done'>('upload');
  const [columns, setColumns] = useState<string[]>([]);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [mapping, setMapping] = useState<ColumnMapping>({
    name: '', street: '', zipCode: '', city: '',
    usageType: '', usageSubType: '', owner: '', description: ''
  });
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [importResult, setImportResult] = useState<any>(null);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isPointreef = ['SUPER_ADMIN', 'ADMIN', 'MANAGER'].includes(user.role);

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const wb = XLSX.read(data, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json: ParsedRow[] = XLSX.utils.sheet_to_json(ws, { defval: '' });

      if (json.length === 0) return;

      const cols = Object.keys(json[0]);
      setColumns(cols);
      setRows(json);
      setMapping(autoDetectMapping(cols));
      setStep('mapping');
    };
    reader.readAsArrayBuffer(file);
  };

  const getMappedProjects = (): MappedProject[] => {
    return rows.map(row => ({
      name: mapping.name ? String(row[mapping.name] || '') : '',
      street: mapping.street ? String(row[mapping.street] || '') : '',
      zipCode: mapping.zipCode ? String(row[mapping.zipCode] || '') : '',
      city: mapping.city ? String(row[mapping.city] || '') : '',
      usageType: mapping.usageType ? String(row[mapping.usageType] || '') : '',
      usageSubType: mapping.usageSubType ? String(row[mapping.usageSubType] || '') : '',
      owner: mapping.owner ? String(row[mapping.owner] || '') : '',
      description: mapping.description ? String(row[mapping.description] || '') : '',
    })).filter(p => p.name && p.street)
  }

  const handleImport = async () => {
    const projects = getMappedProjects()
    if (projects.length === 0) return

    setStep('importing')
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/projects/import`, {
        projects,
        customerId: selectedCustomerId || null
      }, { headers: { Authorization: `Bearer ${token}` } })

      setImportResult(res.data)
      setStep('done')
      onImported()
    } catch (err) {
      console.error(err)
      setStep('preview')
    }
  }

  const mappedProjects = step === 'preview' || step === 'done' ? getMappedProjects() : []

  return (
    <div style={s.overlay}>
      <div style={s.modal}>

        {/* Header */}
        <div style={s.header}>
          <h2 style={s.title}>Liegenschaften importieren</h2>
          <button onClick={onClose} style={s.closeBtn}>✕</button>
        </div>

        {/* Step: Upload */}
        {step === 'upload' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
              <button onClick={downloadTemplate} style={s.templateBtn}>
                Vorlage herunterladen (.xlsx)
              </button>
            </div>
            <div
              style={{ ...s.dropzone, borderColor: dragOver ? '#00c49a' : '#ddd', backgroundColor: dragOver ? '#f0faf7' : '#fafafa' }}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
              />
              <p style={{ fontSize: '32px', margin: '0 0 8px 0' }}>📊</p>
              <p style={{ color: '#555', margin: '0 0 4px 0', fontSize: '14px' }}>Excel oder CSV hier hineinziehen</p>
              <p style={{ color: '#aaa', margin: '0', fontSize: '12px' }}>Unterstützt: .xlsx, .xls, .csv</p>
            </div>
          </div>
        )}

        {/* Step: Mapping */}
        {step === 'mapping' && (
          <div>
            <p style={{ color: '#666', fontSize: '14px', margin: '0 0 16px 0' }}>
              {rows.length} Zeilen erkannt. Ordne die Spalten den Feldern zu:
            </p>
            <div style={s.mappingGrid}>
              {Object.entries(FIELD_LABELS).map(([field, label]) => (
                <div key={field} style={s.mappingRow}>
                  <label style={s.mappingLabel}>{label}</label>
                  <select
                    style={s.mappingSelect}
                    value={mapping[field as keyof ColumnMapping]}
                    onChange={e => setMapping(prev => ({ ...prev, [field]: e.target.value }))}
                  >
                    <option value="">– nicht importieren –</option>
                    {columns.map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            {isPointreef && customers.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <label style={s.mappingLabel}>Kunde *</label>
                <select style={s.mappingSelect} value={selectedCustomerId} onChange={e => setSelectedCustomerId(e.target.value)}>
                  <option value="">Bitte wählen...</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            <div style={s.actions}>
              <button onClick={() => setStep('upload')} style={s.cancelBtn}>Zurück</button>
              <button
                onClick={() => setStep('preview')}
                disabled={!mapping.name || !mapping.street || (isPointreef && !selectedCustomerId)}
                style={{ ...s.primaryBtn, opacity: (!mapping.name || !mapping.street || (isPointreef && !selectedCustomerId)) ? 0.5 : 1 }}
              >
                Vorschau anzeigen
              </button>
            </div>
          </div>
        )}

        {/* Step: Preview */}
        {step === 'preview' && (
          <div>
            <p style={{ color: '#666', fontSize: '14px', margin: '0 0 16px 0' }}>
              {mappedProjects.length} von {rows.length} Zeilen werden importiert (nur Zeilen mit Name und Straße):
            </p>
            <div style={s.previewTable}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={s.th}>Name</th>
                    <th style={s.th}>Adresse</th>
                    <th style={s.th}>Nutzungsart</th>
                    <th style={s.th}>Eigentümer</th>
                  </tr>
                </thead>
                <tbody>
                  {mappedProjects.slice(0, 10).map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={s.td}>{p.name}</td>
                      <td style={s.td}>{p.street}, {p.zipCode} {p.city}</td>
                      <td style={s.td}>{p.usageType}{p.usageSubType ? ` · ${p.usageSubType}` : ''}</td>
                      <td style={s.td}>{p.owner}</td>
                    </tr>
                  ))}
                  {mappedProjects.length > 10 && (
                    <tr>
                      <td colSpan={4} style={{ ...s.td, color: '#aaa', textAlign: 'center' }}>
                        + {mappedProjects.length - 10} weitere...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p style={{ color: '#888', fontSize: '12px', margin: '8px 0 16px 0' }}>
              Koordinaten werden automatisch per Geocoding ermittelt (ca. 1 Sekunde pro Liegenschaft).
            </p>
            <div style={s.actions}>
              <button onClick={() => setStep('mapping')} style={s.cancelBtn}>Zurück</button>
              <button onClick={handleImport} style={s.primaryBtn}>
                {mappedProjects.length} Liegenschaften importieren
              </button>
            </div>
          </div>
        )}

        {/* Step: Importing */}
        {step === 'importing' && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ fontSize: '36px', margin: '0 0 16px 0' }}>⏳</p>
            <p style={{ color: '#555', fontSize: '16px', margin: '0 0 8px 0' }}>Import läuft...</p>
            <p style={{ color: '#aaa', fontSize: '13px', margin: '0' }}>
              Geocoding für {mappedProjects.length} Liegenschaften – bitte warten.
            </p>
          </div>
        )}

        {/* Step: Done */}
        {step === 'done' && importResult && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '36px', margin: '0 0 16px 0' }}>✅</p>
            <p style={{ fontSize: '18px', fontWeight: '600', color: '#0f2744', margin: '0 0 8px 0' }}>
              Import abgeschlossen
            </p>
            <p style={{ color: '#666', fontSize: '14px', margin: '0 0 16px 0' }}>
              {importResult.imported} Liegenschaften erfolgreich importiert.
              {importResult.errors > 0 && ` ${importResult.errors} Fehler.`}
            </p>
            {importResult.errors > 0 && (
              <div style={{ textAlign: 'left', backgroundColor: '#fff5f5', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                {importResult.errors_list?.map((e: any, i: number) => (
                  <p key={i} style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#e74c3c' }}>
                    {e.name}: {e.error}
                  </p>
                ))}
              </div>
            )}
            <button onClick={onClose} style={s.primaryBtn}>Schließen</button>
          </div>
        )}
      </div>
    </div>
  );
};

const s: { [key: string]: React.CSSProperties } = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '32px', width: '680px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { margin: '0', fontSize: '20px', color: '#0f2744' },
  closeBtn: { background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#aaa' },
  templateBtn: { backgroundColor: 'transparent', border: '1px solid #00c49a', color: '#00c49a', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  dropzone: { border: '2px dashed', borderRadius: '12px', padding: '48px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s' },
  mappingGrid: { display: 'flex', flexDirection: 'column', gap: '10px' },
  mappingRow: { display: 'grid', gridTemplateColumns: '160px 1fr', alignItems: 'center', gap: '12px' },
  mappingLabel: { fontSize: '13px', fontWeight: '600', color: '#333' },
  mappingSelect: { padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', fontFamily: 'inherit' },
  actions: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' },
  cancelBtn: { padding: '8px 20px', borderRadius: '6px', border: '1px solid #ddd', backgroundColor: 'white', cursor: 'pointer', fontSize: '14px' },
  primaryBtn: { padding: '8px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#00c49a', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  previewTable: { border: '1px solid #f0f0f0', borderRadius: '8px', overflow: 'hidden', maxHeight: '300px', overflowY: 'auto' },
  th: { padding: '8px 12px', textAlign: 'left' as const, fontWeight: '600', color: '#555', fontSize: '12px' },
  td: { padding: '8px 12px', color: '#333' },
};

export default ProjectImport;



